import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from"@/components/ui/table";
import { Plus, Key, Trash2, Lock, Pencil } from"lucide-react";
import { toast } from"sonner";

const UsersPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [users, setUsers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [createDialogOpen, setCreateDialogOpen] = useState(false);
 const [resetDialogOpen, setResetDialogOpen] = useState(false);
 const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
 const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
 const [editUserData, setEditUserData] = useState({ name: "", email: "" });
 const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
 const [selectedUser, setSelectedUser] = useState(null);
 const [formData, setFormData] = useState({
 name:"",
 email:"",
 role:"engineer",
 password:"",
 });
 const [resetPassword, setResetPassword] = useState("");
 const [changePasswordData, setChangePasswordData] = useState({
 currentPassword:"",
 newPassword:"",
 confirmPassword:"",
 });

 useEffect(() => {
 loadUsers();
 }, []);

 const loadUsers = async () => {
 try {
 const response = await axios.get("/users");
 setUsers(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleCreateUser = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/users", formData);
 toast.success(t("messages.saveSuccess"));
 setCreateDialogOpen(false);
 setFormData({ name:"", email:"", role:"engineer", password:""});
 loadUsers();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create user");
 }
 };

 const handleResetPassword = async (e) => {
 e.preventDefault();
 if (resetPassword.length < 8) {
 toast.error(t("validation.minLength", { count: 8 }));
 return;
 }
 try {
 await axios.post(`/users/${selectedUser.id}/reset-password`, {
 password: resetPassword,
 });
 toast.success(t("messages.saveSuccess"));
 setResetDialogOpen(false);
 setResetPassword("");
 setSelectedUser(null);
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to reset password");
 }
 };

 const handleDeleteUser = async () => {
 try {
 await axios.delete(`/users/${selectedUser.id}`);
 toast.success(t("messages.saveSuccess"));
 setDeleteDialogOpen(false);
 setSelectedUser(null);
 loadUsers();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to delete user");
 }
 };

 const handleEditName = async (e) => {
 e.preventDefault();
 if (!editUserData.name.trim() && !editUserData.email.trim()) return;
 try {
 await axios.patch(`/users/${selectedUser.id}`, {
 ...(editUserData.name.trim() && { name: editUserData.name.trim() }),
 ...(editUserData.email.trim() && { email: editUserData.email.trim() })
 });
 toast.success("User updated");
 setEditUserDialogOpen(false);
 loadUsers();
 } catch (err) { toast.error(err.response?.data?.detail || "Failed to update user"); }
 };

 const handleChangePassword = async (e) => {
 e.preventDefault();
 if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
 toast.error(t("validation.passwordMismatch"));
 return;
 }
 if (changePasswordData.newPassword.length < 8) {
 toast.error(t("validation.minLength", { count: 8 }));
 return;
 }
 try {
 await axios.post("/auth/change-password", {
 current_password: changePasswordData.currentPassword,
 new_password: changePasswordData.newPassword,
 });
 toast.success(t("messages.saveSuccess"));
 setChangePasswordDialogOpen(false);
 setChangePasswordData({ currentPassword:"", newPassword:"", confirmPassword:""});
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to change password");
 }
 };

 const roleColors = {
 owner:"bg-violet-500/10 text-violet-700 border-purple-200",
 management:"bg-blue-500/10 text-blue-700 border-blue-200/60",
 engineer:"bg-emerald-500/10 text-emerald-700 border-green-200/60",
 contractor:"bg-amber-500/10 text-amber-700 border-orange-200/60",
 sales:"bg-pink-100 text-pink-800 border-pink-200",
 accountant:"bg-cyan-100 text-cyan-800 border-cyan-200",
 hr:"bg-emerald-100 text-emerald-800 border-emerald-200/60",
 attorney:"bg-indigo-100 text-indigo-800 border-indigo-200",
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="users-page"className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 Users & Access
 </h1>
 <p className="text-slate-600">Manage user accounts and permissions</p>
 </div>
 <div className="flex gap-2">
 <Button 
 variant="outline"
 onClick={() => setChangePasswordDialogOpen(true)}
 data-testid="change-password-button"
 >
 <Lock className="w-4 h-4 mr-2"/>
 Change My Password
 </Button>
 {user.role ==="owner"&& (
 <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="create-user-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 Create User
 </Button>
 </DialogTrigger>
 <DialogContent data-testid="create-user-dialog">
 <DialogHeader>
 <DialogTitle>Create New User</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleCreateUser} className="space-y-4">
 <div>
 <Label htmlFor="name">Full Name</Label>
 <Input
 id="name"
 data-testid="user-name-input"
 value={formData.name}
 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="email">Email</Label>
 <Input
 id="email"
 type="email"
 data-testid="user-email-input"
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="role">Role</Label>
 <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
 <SelectTrigger data-testid="user-role-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="owner">Owner</SelectItem>
 <SelectItem value="management">Management</SelectItem>
 <SelectItem value="engineer">Engineer</SelectItem>
 <SelectItem value="contractor">Contractor</SelectItem>
 <SelectItem value="sales">Sales</SelectItem>
 <SelectItem value="accountant">Accountant</SelectItem>
 <SelectItem value="hr">HR Manager</SelectItem>
 <SelectItem value="attorney">Attorney</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="password">Initial Password</Label>
 <Input
 id="password"
 type="password"
 data-testid="user-password-input"
 value={formData.password}
 onChange={(e) => setFormData({ ...formData, password: e.target.value })}
 placeholder="Min. 8 characters"
 required
 minLength={8}
 />
 </div>
 <Button
 type="submit"
 data-testid="submit-user-button"
 className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 >
 Create User
 </Button>
 </form>
 </DialogContent>
 </Dialog>
 )}
 </div>
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading users...</div>
 </div>
 ) : (
 <Card>
 <CardHeader>
 <CardTitle>All Users</CardTitle>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Name</TableHead>
 <TableHead>Email</TableHead>
 <TableHead>Role</TableHead>
 <TableHead>Status</TableHead>
 <TableHead className="text-right">Actions</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {users.map((u) => (
 <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
 <TableCell className="font-medium">{u.name}</TableCell>
 <TableCell>{u.email}</TableCell>
 <TableCell>
 <Badge variant="outline"className={roleColors[u.role]}>
 {u.role}
 </Badge>
 </TableCell>
 <TableCell>
 <Badge variant={u.is_active ?"default":"secondary"}>
 {u.is_active ?"Active":"Inactive"}
 </Badge>
 </TableCell>
 <TableCell className="text-right">
 {user.role ==="owner"&& (
 <div className="flex gap-2 justify-end">
 <Button
 size="sm"
 variant="outline"
 className="rounded-sm text-xs"
 onClick={() => {
 setSelectedUser(u);
 setEditUserData({ name: u.name, email: u.email });
 setEditUserDialogOpen(true);
 }}
 data-testid={`edit-name-${u.id}`}
 >
 <Pencil className="w-3.5 h-3.5 mr-1"/>
 Edit
 </Button>
 <Button
 size="sm"
 variant="outline"
 className="rounded-sm text-xs"
 onClick={() => {
 setSelectedUser(u);
 setResetDialogOpen(true);
 }}
 data-testid={`reset-password-${u.id}`}
 >
 <Key className="w-3.5 h-3.5 mr-1"/>
 Password
 </Button>
 {u.id !== user.id && u.role !=="owner"&& (
 <Button
 size="sm"
 variant="outline"
 className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50/80"
 onClick={() => {
 setSelectedUser(u);
 setDeleteDialogOpen(true);
 }}
 data-testid={`delete-user-${u.id}`}
 >
 <Trash2 className="w-3.5 h-3.5 mr-1"/>
 Delete
 </Button>
 )}
 </div>
 )}
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 )}
 </div>

 {/* Reset Password Dialog */}
 <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleResetPassword} className="space-y-4">
 <div>
 <Label htmlFor="new-password">New Password</Label>
 <Input
 id="new-password"
 type="password"
 data-testid="new-password-input"
 value={resetPassword}
 onChange={(e) => setResetPassword(e.target.value)}
 placeholder="Min. 8 characters"
 required
 minLength={8}
 />
 </div>
 <Button
 type="submit"
 data-testid="submit-reset-password"
 className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 >
 Reset Password
 </Button>
 </form>
 </DialogContent>
 </Dialog>

 {/* Delete User Confirmation Dialog */}
 <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Delete User</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <p className="text-slate-600">
 Are you sure you want to delete <span className="font-semibold">{selectedUser?.name}</span> ({selectedUser?.email})?
 </p>
 <p className="text-sm text-red-600">
 This action cannot be undone.
 </p>
 <div className="flex gap-2 justify-end">
 <Button
 variant="outline"
 onClick={() => setDeleteDialogOpen(false)}
 >
 Cancel
 </Button>
 <Button
 className="bg-red-600 hover:bg-red-700"
 onClick={handleDeleteUser}
 data-testid="confirm-delete-user"
 >
 Delete User
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Change My Password Dialog */}
 <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Change Your Password</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleChangePassword} className="space-y-4">
 <div>
 <Label htmlFor="current-password">Current Password</Label>
 <Input
 id="current-password"
 type="password"
 data-testid="current-password-input"
 value={changePasswordData.currentPassword}
 onChange={(e) => setChangePasswordData({ ...changePasswordData, currentPassword: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="new-password-change">New Password</Label>
 <Input
 id="new-password-change"
 type="password"
 data-testid="new-password-change-input"
 value={changePasswordData.newPassword}
 onChange={(e) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
 placeholder="Min. 8 characters"
 required
 minLength={8}
 />
 </div>
 <div>
 <Label htmlFor="confirm-password">Confirm New Password</Label>
 <Input
 id="confirm-password"
 type="password"
 data-testid="confirm-password-input"
 value={changePasswordData.confirmPassword}
 onChange={(e) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
 placeholder="Repeat new password"
 required
 minLength={8}
 />
 </div>
 <Button
 type="submit"
 data-testid="submit-change-password"
 className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 >
 Change Password
 </Button>
 </form>
 </DialogContent>
 </Dialog>

 {/* Edit User Dialog — name + email */}
 <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle className="font-display uppercase tracking-tight text-slate-900">Edit User</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleEditName} className="space-y-4">
 <div>
 <Label>Full Name</Label>
 <Input className="mt-1 rounded-sm" value={editUserData.name}
 onChange={(e) => setEditUserData(p => ({ ...p, name: e.target.value }))}
 placeholder="First and last name" required />
 </div>
 <div>
 <Label>Email</Label>
 <Input className="mt-1 rounded-sm" type="email" value={editUserData.email}
 onChange={(e) => setEditUserData(p => ({ ...p, email: e.target.value }))}
 placeholder="email@example.com" required />
 </div>
 <div className="flex gap-3">
 <Button type="button" variant="outline" className="flex-1 rounded-sm" onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
 <Button type="submit" className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800">Save</Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 </Layout>
 );
};

export default UsersPage;
